import { useGetRehabForms, useGetTransitionalCare } from "apollo/hooks/queries";
import { isLoading } from "apollo/utils";
import { DOCS_FORM_NAMES } from "core/consts";
import { getPatientName } from "core/model/patients";
import {
  Auction,
  Patient,
  QueryProgress,
  RehabForms,
  TransitionalCare,
  ValueOf,
} from "core/types";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "ds/ui";
import {
  IMPORT_AUCTION_ID,
  REIMPORT_AUCTION_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import { RefreshCwIcon, SparklesIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslations } from "translations";
import { useRecareDocsContext } from "../context";
import { FormFooter } from "./Footer";
import { DocsFormFields } from "./FormFields";
import { DocsFormDataMapped, DocsFormName } from "./Forms";
import { DocsModalSources } from "./Sources";
import { useDocsForm } from "./useDocsForm";

export const DOCS_FORM_FOOTER_SCREENS = {
  DEFAULT: "default",
  DELETE_CONFIRMATION: "delete-confirmation",
  SAVE_CONFIRMATION: "save-confirmation",
  WARN_OPEN_PDF: "warn-open-pdf",
  WARN_SEND_TO_HIS: "warn-send-to-his",
} as const;

export type DocsFormFooterScreen = ValueOf<typeof DOCS_FORM_FOOTER_SCREENS>;

const DocsModalHeader = ({
  header,
  importFormData,
  importProgress,
  isFormFilled,
  patient,
}: {
  importFormData: () => void;
  importProgress: QueryProgress;
  isFormFilled: boolean;
  patient: Patient;
  header: string;
}) => {
  const { setFormModal } = useRecareDocsContext();
  const translations = useTranslations();

  return (
    <ModalHeader className="flex w-full items-center justify-between">
      <div className="flex flex-col gap-1">
        <p className="text-body-md font-semibold text-gray-600">{`${getPatientName(
          patient,
        )} (${patient.user_id})`}</p>
        <h1 className="text-heading-lg font-bold">{header}</h1>
      </div>
      <div className="flex gap-4">
        <Button
          color="primary"
          id={isFormFilled ? REIMPORT_AUCTION_ID : IMPORT_AUCTION_ID}
          isLoading={isLoading(importProgress)}
          size="sm"
          startContent={isFormFilled ? <RefreshCwIcon /> : <SparklesIcon />}
          variant={isFormFilled ? "ghost" : "solid"}
          onPress={() => {
            importFormData();
          }}
        >
          {isFormFilled
            ? translations.extractDocs.splitView.header.buttonRefreshData
            : translations.extractDocs.splitView.header.buttonImportData}
        </Button>
        <Button
          color="primary"
          endContent={<XIcon />}
          id="close-modal"
          size="sm"
          variant="light"
          onPress={() => setFormModal(null)}
        >
          {translations.extractDocs.splitView.header.buttonClose}
        </Button>
      </div>
    </ModalHeader>
  );
};

const DocsModalContent = ({
  auction,
  formName,
  patient,
  rehabForms,
  transitionalCare,
}: {
  formName: DocsFormName;
  auction: Auction;
  patient: Patient;
  rehabForms: RehabForms | undefined;
  transitionalCare: TransitionalCare | undefined;
}) => {
  const translations = useTranslations();

  const {
    config,
    confirmationChecked,
    footerBanner,
    footerScreen,
    form,
    handleBack,
    handleDelete,
    handleDeleteProgress,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasPreviousFormValue,
    headerBanner,
    importFormData,
    importProgress,
    isFormFilled,
    methods,
    onCloseHeaderBanner,
    onSubmitProgress,
    progressOpenPdf,
    progressSendToHis,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    skipRequiredValidation,
  } = useDocsForm({
    auction,
    formName,
    patient,
    rehabForms,
    transitionalCare,
  });

  const [sourceSelected, setSourceSelected] = useState<
    keyof DocsFormDataMapped<typeof formName> | undefined
  >(undefined);

  return (
    <FormProvider {...methods}>
      <Form className="gap-0" onSubmit={handleSubmit}>
        <DocsModalHeader
          header={config.header({ translations })}
          importFormData={importFormData}
          importProgress={importProgress}
          isFormFilled={isFormFilled}
          patient={patient}
        />
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex w-full flex-row gap-0 overflow-hidden p-0">
          <div
            // TODO: not ideal, fix
            className="max-h-[calc(95vh-80px)] w-[50%] overflow-y-auto"
          >
            <DocsFormFields<typeof formName>
              auction={auction}
              config={config}
              headerBanner={headerBanner}
              importData={importFormData}
              setSourceSelected={setSourceSelected}
              skipRequiredValidation={skipRequiredValidation}
              sourceSelected={sourceSelected}
              onCloseHeaderBanner={onCloseHeaderBanner}
            />
            <FormFooter
              confirmationChecked={confirmationChecked}
              footerBanner={footerBanner}
              footerScreen={footerScreen}
              handleOpenPdf={handleOpenPdf}
              handleSendToHis={handleSendToHis}
              hasPreviousFormValue={hasPreviousFormValue}
              progressOpenPdf={progressOpenPdf}
              progressSendToHis={progressSendToHis}
              setConfirmationChecked={setConfirmationChecked}
              setFooterBanner={setFooterBanner}
              setFooterScreen={setFooterScreen}
              updatedAt={
                formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE
                  ? transitionalCare?.updated_at
                  : form?.updated_at
              }
              onBack={handleBack}
              onDelete={handleDelete}
              onDeleteProgress={handleDeleteProgress}
              onSubmitProgress={onSubmitProgress}
            />
          </div>
          <DocsModalSources auction={auction} patient={patient} />
        </ModalBody>
      </Form>
    </FormProvider>
  );
};

export const RecareDocsModal = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const { formModal } = useRecareDocsContext();

  const [rehabFormsQueryProgress, rehabForms] = useGetRehabForms({
    auctionId: auction.id,
    options: { skip: formModal === DOCS_FORM_NAMES.TRANSITIONAL_CARE },
    patientId: patient.id,
  });

  const [transitionalCareQueryProgress, transitionalCare] =
    useGetTransitionalCare({
      auctionId: auction.id,
      options: {
        skip: formModal !== DOCS_FORM_NAMES.TRANSITIONAL_CARE,
      },
      patientId: patient.id,
    });

  return (
    <Modal hideCloseButton isOpen={!!formModal}>
      <ModalContent className="flex min-h-[95vh] w-full min-w-[95vw] flex-col">
        {formModal &&
          (isLoading(rehabFormsQueryProgress) ||
          isLoading(transitionalCareQueryProgress) ? (
            <ModalBody className="flex h-full w-full items-center justify-center">
              <Spinner
                color="primary"
                size="lg"
                testId="spinner-loading-docs"
              />
            </ModalBody>
          ) : (
            <DocsModalContent
              auction={auction}
              formName={formModal}
              patient={patient}
              rehabForms={rehabForms}
              transitionalCare={transitionalCare}
            />
          ))}
      </ModalContent>
    </Modal>
  );
};
